import type { Action, WorkOrder } from '../App/types';
import * as ActionTypes from './constants';
import { pathOr } from 'ramda';

export function createWorkOrder(
  workOrder: WorkOrder,
  print: boolean,
): Action<Object> {
  return {
    type: ActionTypes.CREATE_WORK_ORDER,
    payload: {
      workOrder,
      print,
    },
  };
}

export function createWorkOrderSuccess(
  detailIdReadable: string,
  createdAt: string,
): Action<any> {
  return {
    type: ActionTypes.CREATE_WORK_ORDER_SUCCESS,
    payload: {
      detailIdReadable,
      createdAt,
    },
  };
}

export function getAllUnits(): Action<any> {
  return {
    type: ActionTypes.GET_ALL_UNITS,
    payload: undefined,
  };
}

export function getAllUnitsError(error: Error): Action<Error> {
  return {
    type: ActionTypes.GET_ALL_UNITS_ERROR,
    payload: error,
  };
}

// Carried on every option so the work order form can show the accessibility of
// whichever unit is selected. Already alphabetical from the API.
const unitAccessibilityOf = (unit: Object) => ({
  isADA: unit.isADA ?? false,
  accessibilityTypes: unit.accessibilityTypes ?? [],
});

const vacantUnitOption = (unit: Object) => ({
  id: unit.id,
  unitId: unit.id,
  status: unit.unitStatus?.description,
  householdId: '',
  description: `${unit.number}`,
  ...unitAccessibilityOf(unit),
  resident: {
    customerId: '',
    name: '',
    emailAddress: '',
    phoneNumber: '',
  },
});

// Null when the resident isn't a current one, so the caller just skips it.
const residentUnitOption = (unit: Object, resident: Object) => {
  const customer = pathOr('', ['rc', 'customer'], resident);
  const customerId = pathOr('', ['id'], customer);
  const householdId = pathOr('', ['householdId'], resident);
  const isValidEmail = pathOr(null, ['isValidEmail'], customer);
  const isOptedOut = pathOr(null, ['isOptedOut'], customer);
  const firstName = pathOr(null, ['firstName'], customer);
  const lastName = pathOr(null, ['lastName'], customer);
  const name = firstName || lastName ? `${firstName} ${lastName}`.trim() : '';
  const status = pathOr(null, ['status'], customer);

  if (!(unit.id && name && unit.number && status === 'Current Resident')) {
    return null;
  }

  return {
    id: customerId,
    unitId: unit.id,
    householdId,
    status: unit.unitStatus?.description,
    description: `${unit.number} - ${name}`,
    ...unitAccessibilityOf(unit),
    resident: {
      customerId,
      name,
      emailAddress: pathOr(null, ['emailAddress'], customer),
      phoneNumber: pathOr(null, ['phoneNumber'], customer),
      householdId,
      isValidEmail,
      isOptedOut,
    },
  };
};

export function getAllUnitsSuccess(units: Array<Object>): Action<any> {
  const unitOptions = units.reduce((acc, unit) => {
    const currentAssignation = pathOr(
      [],
      ['currentAssignation', 'lease', 'resident'],
      unit,
    );

    if (currentAssignation.length === 0) {
      acc.push(vacantUnitOption(unit));
      return acc;
    }

    currentAssignation.forEach((resident) => {
      const option = residentUnitOption(unit, resident);
      if (option) acc.push(option);
    });
    return acc;
  }, []);
  return {
    type: ActionTypes.GET_ALL_UNITS_SUCCESS,
    payload: unitOptions,
  };
}

export function getWorkOrder(workOrderId: any): Action<any> {
  return {
    type: ActionTypes.GET_WORK_ORDER,
    payload: workOrderId,
  };
}

export function getWorkOrderSuccess(workOrder: WorkOrder): Action<any> {
  return {
    type: ActionTypes.GET_WORK_ORDER_SUCCESS,
    payload: workOrder,
  };
}

export function getWorkOrderError(error: any): Action<any> {
  return {
    type: ActionTypes.GET_WORK_ORDER_ERROR,
    payload: error,
  };
}

export function saveWorkOrder(
  workOrder: WorkOrder,
  print: boolean,
): Action<Object> {
  return {
    type: ActionTypes.SAVE_WORK_ORDER,
    payload: {
      workOrder,
      print,
    },
  };
}

export function printWorkOrder(detailId: string): Action<string> {
  return {
    type: ActionTypes.PRINT_WORK_ORDER,
    payload: detailId,
  };
}

export function saveWorkOrderSuccess(): Action<any> {
  return {
    type: ActionTypes.SAVE_WORK_ORDER_SUCCESS,
    payload: undefined,
  };
}

export function clearLoadedWorkOrder(): Action<any> {
  return {
    type: ActionTypes.CLEAR_LOADED_WORK_ORDER,
    payload: undefined,
  };
}
